import React, { useEffect, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useObservable } from '@ngneat/react-rxjs';
import { useEffectFn } from '@ngneat/effects-hooks';
import { finalize, Observable } from 'rxjs';
import { useSnackbar } from 'notistack';
import { DocumentKind } from '@store/files';
import {Document, documentsQuery, documentsService, ManageDocument, searchDocumentsEffect} from '@store/documents';

import SearchComponent from '@components/inputs/search/Search.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import NewTableComponent from '@components/table/NewTable.component';

import COLUMN_TYPES from '@utils/table/columnTypes.table';
import { ConfirmActionProps, OtherActionType } from '@utils/table/columnTypes/actions.columnType';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import SelectComponent from '@components/inputs/select/Select.component';
import { emitOnce } from '@ngneat/elf';
import ConfirmModal from '@components/modal/Confirm.modal';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';

const DocumentsTable = () => {
  const { t } = useTranslation();
  const { supplierId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const searchDocuments = useEffectFn(searchDocumentsEffect);

  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);

  const [{ documents, loading }] = useObservable(documentsQuery.documents$);

  const [filters] = useObservable(documentsQuery.filters$);
  const [sortModel] = useObservable(documentsQuery.sort$);
  const [tablePagination] = useObservable(documentsQuery.pagination$);

  useEffect(() => {
    if (supplierId) documentsService.resetFilters(undefined, supplierId);
  }, [supplierId]);

  useEffect(() => {
    searchDocuments({ filters, tablePagination, sort: sortModel });
  }, [filters, searchDocuments, sortModel, tablePagination]);

  const handleConfirmValidation = (type: OtherActionType, document: Document) => (handleStopLoading: () => void) => {
    let validationObservable: Observable<any> | undefined = undefined;
    switch (type) {
      case OtherActionType.DELETE:
        validationObservable = documentsService.deleteDocument(document.id);
        break;
    }
    validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
      next: () => {
        enqueueSnackbar(t(`${OtherActionType.translationKey(type)}.success.document`), { variant: 'success' });
        setOpenConfirmAction(false);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <Stack flex={1} spacing={2} pb="25px">
      <Stack direction="row" spacing={2} alignItems="center">
        <Box>
          <DatePickerComponent
            size="small"
            inputWidth={150}
            placeholder={t('documents.filters.importDate')}
            value={filters.importDate}
            handleChange={documentsService.setFilters('importDate')} />
        </Box>
        <Box>
          <SelectComponent
            size="small"
            inputWidth={170}
            items={DocumentKind.selectItems()}
            value={filters.documentKind}
            handleChange={documentsService.setFilters('documentKind')}
            placeholder={t('documents.filters.documentKind')} />
        </Box>
      </Stack>
      <Stack flex={1} spacing={2} pb="25px">
        <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
          <Box width="500px">
            <SearchComponent
              value={filters.search}
              handleChange={(value) => {
                emitOnce(() => {
                  documentsService.setFilters('search')(value);
                  documentsService.setPagination('page')(1);
                });
              }} />
          </Box>
          <TableFiltersComponent
            filters={filters}
            handleSave={documentsService.setFilters('tableFilters')}
            handleReset={() => supplierId ? documentsService.resetFilters(undefined, supplierId) : undefined} />
        </Stack>
        <NewTableComponent
          loading={loading}
          columns={[
            COLUMN_TYPES.TEXT_BOLD({
              field: 'document.name',
              label: t('documents.table.name'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, ManageDocument>) => params.row.name,
              fullWidth: true,
              minWidth: 200,
            }),
            COLUMN_TYPES.SELECT({
              field: 'document.documentKind',
              label: t('documents.table.type'),
              options: DocumentKind.selectItems(),
              valueGetter: (params: GridValueGetterParams<any, ManageDocument>) => params.row.documentKind,
              sortable: true,
              width: 150,
            }),
            COLUMN_TYPES.TEXT_BOLD({
              field: 'organisationUnitName',
              label: t('documents.table.site'),
              valueGetter: (params: GridValueGetterParams<any, ManageDocument>) => params.row.organisationUnitName,
              // sortable: true,
              fullWidth: true,
              minWidth: 350,
            }),

            COLUMN_TYPES.TEXT({
              field: 'adminDocument.startValidity',
              color: 'secondary',
              label: t('documents.table.begin'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, ManageDocument>) => params.row.startValidity,
              valueFormatter: (params) => params.value ? t('global.date', {date: new Date(params.value)}) : '-',
              width: 130,
            }),
            COLUMN_TYPES.TEXT({
              field: 'adminDocument.endValidity',
              color: 'secondary',
              label: t('documents.table.end'),
              sortable: true,
              valueGetter: (params: GridValueGetterParams<any, ManageDocument>) => params.row.endValidity,
              valueFormatter: (params) => params.value ? t('global.date', {date: new Date(params.value)}) : '-',
              width: 130,
            }),
            COLUMN_TYPES.TEXT({
              field: 'document.author',
              label: t('documents.table.author'),
              sortable: true,
              fullWidth: true,
              valueGetter: (params: GridValueGetterParams<any, ManageDocument>) => params.row.author,
              minWidth: 200,
            }),
            COLUMN_TYPES.ACTIONS({
              handleShowRow: (document: Document) => navigate(`${document.id}`),
              otherActions: [
                {
                  type: OtherActionType.DELETE,
                  handleClick: (document: Document) => setOpenConfirmAction({
                    type: OtherActionType.DELETE,
                    row: document
                  }),
                },
              ]
            }),
          ]}
          disableRowSelection
          handlePageSizeChange={documentsService.setPagination('limit')}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => documentsService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          handleSortModelChange={documentsService.setSort}
          sortModel={[sortModel]}
          handleReload={() => searchDocuments({ filters, tablePagination, sort: sortModel })}
          totalRows={documentsQuery.totalRows}
          data={documents} />
        {!!openConfirmAction && (
          <ConfirmModal
            handleClose={() => setOpenConfirmAction(false)}
            description={t(`${OtherActionType.translationKey(openConfirmAction.type)}.description.document`)}
            handleValidate={handleConfirmValidation(openConfirmAction.type, openConfirmAction.row)}
            title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
            validateButton={OtherActionType.label(openConfirmAction.type)} />
        )}
        <Outlet />
      </Stack>
    </Stack>
  );
};

export default DocumentsTable;
